import { Pagination, Preset } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ColumnDef, DataTable, dataTableHighlight } from '../../Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { PresetPanel } from './PresetPanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { Typography } from '@mui/material';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

const ROW_MIN_HEIGHT = 64;

export const PresetManagementContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { setPanelOpen, togglePanel } = useContentControl();
  const { scrollTop } = useNavigation();
  const { refresh } = usePagination();

  const [, startProcess] = useRequest<void>();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedPreset, setSelectedPreset] = useState<Preset>();

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setDialogOpen(false);
    setSelectedPreset(undefined);
  }, []);

  const fetchPresets = useCallback(async (pagination: Pagination) => await api.getPresets(pagination), []);

  const deletePreset = useCallback(() => {
    startProcess(
      () => api.deletePreset(selectedPreset!.name),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [selectedPreset]);

  const hasWritePermission = useMemo(() => hasPermission(Permissions.PRESETS_WRITE), [hasPermission]);

  const columns = useMemo<ColumnDef[]>(
    () => [
      {
        header: 'Name',
        body: (entry: Preset, searchText?: string) => (
          <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
            {dataTableHighlight(entry.name, [searchText ?? ''])}
          </ScrollableStack>
        ),
        filterField: 'name',
        sortField: 'P.name',
        proportionalContent: '25%',
      },
      {
        header: 'Coordinate-Prefix',
        body: (entry: Preset, searchText?: string) => (
          <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
            {dataTableHighlight(entry.coordinatePrefix, [searchText ?? ''])}
          </ScrollableStack>
        ),
        sortField: 'N.materializedPath',
        proportionalContent: '35%',
      },
      {
        header: 'Fields',
        body: (entry: Preset, searchText?: string) => {
          const { keys } = entry;
          const text = keys === null || keys.length === 0 ? '-' : keys.join(', ');

          return (
            <ScrollableStack direction={'row'} width={'100%'} minHeight={ROW_MIN_HEIGHT} alignItems={'center'}>
              {dataTableHighlight(text, [searchText ?? ''])}
            </ScrollableStack>
          );
        },
        proportionalContent: '40%',
      },
    ],
    [],
  );

  return (
    <ContentControl
      panel={<PresetPanel preset={selectedPreset} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Preset'}
          text={
            <Typography fontWeight={700} fontSize={14}>
              {selectedPreset?.name}
            </Typography>
          }
          onClose={dismiss}
          onConfirm={deletePreset}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          onClick={togglePanel}
          tooltip={'Add Preset'}
          requiredPermission={Permissions.SHELFLABELING_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchPresets}
        columns={columns}
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: Preset) => {
              scrollTop();
              setSelectedPreset(entry);
              setPanelOpen(true);
            },
            disabled: !hasWritePermission,
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: Preset) => {
              setSelectedPreset(entry);
              setDialogOpen(true);
              setPanelOpen(false);
            },
            disabled: !hasWritePermission,
          },
        ]}
      />
    </ContentControl>
  );
};
